import './CashClosingPendingDetail.scss';
import 'react-datepicker/dist/react-datepicker.css';

import { Col, Row } from 'react-bootstrap';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { faCheckCircle, faWallet } from '@fortawesome/free-solid-svg-icons';

import AppButton from '../../../commons/buttons/AppButton';
import AppCard from '../../../commons/cards/AppCard';
import AppLinkButton from '../../../commons/buttons/AppLinkButton';
import AppLoading from '../../../commons/AppLoading';
import CashClosingPayModal from '../components/CashClosingPayModal';
import CashClosingPendingDetailModal from './CashClosingPendingDetailModal';
import EmployeeAPI from '../../employee/EmployeeAPI';
import EmptyState from '../../../commons/EmptyState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HallAppToast from '../../../commons/HallAppToast';
import MenuTitle from '../../../commons/menu/MenuTitle';
import ReportHeader from '../../report-full/components/ReportHeader';
import { formatInReal } from '../../../commons/util/HallAppUtil';

const employeeAPI = new EmployeeAPI();

const CashClosingPendingDetail = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showModalPayment, setShowModalPayment] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const getDisplayName = useCallback((name = '') => {
    if (!name) return 'Profissional';

    if (name.includes('@')) {
      return name.split('@')[0];
    }

    return name.split(' ')[0];
  }, []);

  const normalizeSummary = useCallback((data = []) => {
    return data
      .map((item) => ({
        employeeId: item.employeeId,
        employeeName: item.employeeName,
        totalToPay: Number(item.amountToPay ?? item.toPay ?? item.consolidatedCommissionPending ?? 0),
        serviceCommissionPending: Number(item.serviceToPay ?? 0),
        productCommissionPending: Number(item.productToPay ?? 0),
        totalAdvance: Number(item.totalAdvance ?? 0),
      }))
      .filter((item) => item.employeeId);
  }, []);

  const findAllSummary = useCallback(async () => {
    try {
      setLoading(true);

      const resp = await employeeAPI.getSummaryPendingPay();
      const payload = resp?.data || [];
      const normalized = normalizeSummary(payload);

      setPending(normalized);
    } catch (error) {
      console.error('Erro ao buscar resumo de pendências:', error);
      HallAppToast.genericError();
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, [normalizeSummary]);

  useEffect(() => {
    findAllSummary();
  }, [findAllSummary]);

  const totalPendingAmount = useMemo(() => {
    return pending.reduce((acc, item) => acc + (Number(item.totalToPay) || 0), 0);
  }, [pending]);

  const totalEmployees = pending.length;

  const handleOpenDetail = useCallback((employee) => {
    setSelectedEmployee(employee);
    setShowDetailModal(true);
  }, []);

  const handleOpenPayment = useCallback((employee) => {
    setSelectedEmployee(employee);
    setShowModalPayment(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setShowDetailModal(false);
    setSelectedEmployee(null);
  }, []);

  const handleClosePayment = useCallback(() => {
    setShowModalPayment(false);
    setSelectedEmployee(null);
  }, []);

  return (
    <>
      <ReportHeader name="Comissões pendentes" />

      <div className="container cash-closing-pending-page">
        <MenuTitle title="Comissões pendentes" />

        {loading ? (
          <div className="cash-closing-pending-page__loading">
            <AppLoading />
          </div>
        ) : (
          <>
            {!!pending.length && (
              <div className="cash-closing-pending-page__summary">
                <div className="cash-closing-pending-page__summary-card">
                  <div className="cash-closing-pending-page__summary-icon">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>

                  <div className="cash-closing-pending-page__summary-content">
                    <span className="cash-closing-pending-page__summary-label">
                      Total pendente
                    </span>
                    <strong className="cash-closing-pending-page__summary-value">
                      {formatInReal(totalPendingAmount)}
                    </strong>
                  </div>
                </div>

                <div className="cash-closing-pending-page__summary-card">
                  <div className="cash-closing-pending-page__summary-content">
                    <span className="cash-closing-pending-page__summary-label">
                      Profissionais com pendência
                    </span>
                    <strong className="cash-closing-pending-page__summary-value">
                      {totalEmployees}
                    </strong>
                  </div>
                </div>
              </div>
            )}

            <Row className="cash-closing-pending-page__grid">
              {pending.length === 0 ? (
                <EmptyState
                  title="Nenhuma comissão pendente"
                  description="Parabéns! Não há comissões pendentes de pagamento no momento. Todos os valores já foram quitados."
                  icon={<FontAwesomeIcon icon={faCheckCircle} />}
                />
              ) : (
                pending.map((employee) => {
                  const displayName = getDisplayName(employee.employeeName);

                  return (
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      key={employee.employeeId}
                      className="cash-closing-pending-page__col"
                    >
                      <AppCard>
                        <div className="cash-closing-pending-card">
                          <div className="cash-closing-pending-card__header">
                            <div className="cash-closing-pending-card__identity">
                              <div className="cash-closing-pending-card__avatar">
                                {displayName?.charAt(0)?.toUpperCase() || 'P'}
                              </div>

                              <div className="cash-closing-pending-card__identity-text">
                                <strong className="cash-closing-pending-card__name">
                                  {displayName}
                                </strong>
                                <span className="cash-closing-pending-card__subtitle">
                                  Comissão pendente disponível
                                </span>
                              </div>
                            </div>

                            <div className="cash-closing-pending-card__amount">
                              {formatInReal(employee.totalToPay)}
                            </div>
                          </div>

                          <div className="cash-closing-pending-card__body">
                            <div className="cash-closing-pending-card__info">
                              <span>Serviços</span>
                              <strong>
                                {formatInReal(employee.serviceCommissionPending)}
                              </strong>
                            </div>

                            <div className="cash-closing-pending-card__info">
                              <span>Produtos</span>
                              <strong>
                                {formatInReal(employee.productCommissionPending)}
                              </strong>
                            </div>

                            <div className="cash-closing-pending-card__info">
                              <span>Adiantamentos</span>
                              <strong className="cash-closing-pending-card__info--danger">
                                {formatInReal(employee.totalAdvance)}
                              </strong>
                            </div>
                          </div>

                          <div className="cash-closing-pending-card__footer">
                            <AppLinkButton
                              fullWidth
                              name="Ver detalhes"
                              onClick={() => handleOpenDetail(employee)}
                            />

                            <div className="cash-closing-pending-card__pay-button">
                              <AppButton
                                width="100%"
                                name="Marcar como pago"
                                onClick={() => handleOpenPayment(employee)}
                              />
                            </div>
                          </div>
                        </div>
                      </AppCard>
                    </Col>
                  );
                })
              )}
            </Row>
          </>
        )}
      </div>

      {showDetailModal && (
        <CashClosingPendingDetailModal
          show={showDetailModal}
          closeShow={handleCloseDetail}
          employeeId={selectedEmployee?.employeeId}
          employeeName={selectedEmployee?.employeeName}
        />
      )}

      {showModalPayment && (
        <CashClosingPayModal
          payPending={true}
          show={setShowModalPayment}
          findAllSummary={findAllSummary}
          employee={selectedEmployee}
          closeShow={handleClosePayment}
        />
      )}
    </>
  );
};

export default CashClosingPendingDetail;